/**
 * Shared layout for “access not available” full pages (admin-only routes, gated catalog kinds, etc.).
 */

import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link, Typography } from '@material-ui/core';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { PageWithHeader, Content } from '@backstage/core-components';
import { AccessRequestHelp } from './AccessRequestHelp';

export type AccessDeniedPageProps = {
  /** Passed to {@link PageWithHeader} `title` */
  title: string;
  /** Explanatory copy below the “Access not available” heading */
  description: ReactNode;
  /** Text for the link back to `/catalog` */
  catalogLinkLabel?: string;
};

export function AccessDeniedPage({
  title,
  description,
  catalogLinkLabel = 'Return to catalog',
}: AccessDeniedPageProps): JSX.Element {
  return (
    <PageWithHeader title={title} themeId="home">
      <Content noPadding>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="60vh"
          px={3}
          textAlign="center"
        >
          <LockOutlinedIcon
            style={{ fontSize: 64, color: 'rgba(0,0,0,0.35)', marginBottom: 24 }}
          />
          <Typography variant="h5" component="h1" gutterBottom>
            Access not available
          </Typography>
          <Typography
            variant="body1"
            color="textSecondary"
            style={{ maxWidth: 480, marginBottom: 24 }}
          >
            {description}
          </Typography>
          <AccessRequestHelp />
          <Link
            component={RouterLink}
            to="/catalog"
            variant="body1"
            style={{ marginTop: 24 }}
          >
            {catalogLinkLabel}
          </Link>
        </Box>
      </Content>
    </PageWithHeader>
  );
}
